import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { STATUS } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { hoyISO } from "@/lib/zona";

const btn = "min-h-[52px] px-5 rounded-xl font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60";

// "Hecho" con opción de deshacer desde el aviso.
export const useMarcarHecho = () => {
  const { cierreAsunto } = useData();
  return async (m) => {
    try {
      await cierreAsunto(m.id, { accion: "hecho" });
      toast.success(`«${m.title}» marcado como hecho`, {
        action: { label: "Deshacer", onClick: () => cierreAsunto(m.id, { accion: "deshacer" }).then(() => toast.success("Deshecho: vuelve a estar en curso")).catch(() => toast.error("No se pudo deshacer.")) },
        duration: 10000,
      });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo marcar."); }
  };
};

// Recupera un asunto o recordatorio marcado como hecho, con todos sus datos originales.
export const VolverEnCurso = ({ m, testid, className = "" }) => {
  const { cierreAsunto } = useData();
  const [busy, setBusy] = useState(false);
  const volver = async (e) => {
    e.stopPropagation(); setBusy(true);
    try { await cierreAsunto(m.id, { accion: "deshacer" }); toast.success(m.recordatorio_puntual ? "Recordatorio en curso de nuevo" : "Vuelve a estar activo"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo recuperar."); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={testid} disabled={busy} onClick={volver} className={`${btn} bg-[#F2EDE4] text-[#1C2421] border border-[#E5DFD5] ${className}`}>
      <Icons.Undo2 size={18} /> {m.recordatorio_puntual ? "Volver a poner en curso" : "Deshacer «Hecho»"}
    </button>
  );
};

const NuevaFecha = ({ m, onDone }) => {
  const { cierreAsunto } = useData();
  const [f, setF] = useState(""); const [h, setH] = useState(m.due_time || "");
  const [busy, setBusy] = useState(false);
  const guardar = async () => {
    setBusy(true);
    try { await cierreAsunto(m.id, { accion: "nueva_fecha", due_date: f, due_time: h }); toast.success("Nueva fecha guardada"); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
    finally { setBusy(false); }
  };
  const cls = "w-full min-h-[48px] px-3 rounded-xl bg-white border border-[#E5DFD5] text-base";
  return (
    <div className="bg-[#FAF8F5] border border-[#E5DFD5] rounded-xl p-3 space-y-2" data-testid="cierre-nueva-fecha-form">
      <div className="grid grid-cols-2 gap-2">
        <input type="date" min={hoyISO()} data-testid="cierre-nueva-fecha-input" value={f} onChange={(e) => setF(e.target.value)} className={cls} />
        <input type="time" data-testid="cierre-nueva-hora-input" value={h} onChange={(e) => setH(e.target.value)} className={cls} />
      </div>
      <button data-testid="cierre-nueva-fecha-guardar" disabled={!f || busy} onClick={guardar} className={`${btn} w-full bg-[#3A6B56] text-white`}>Guardar nueva fecha</button>
    </div>
  );
};

const TIPOS = [["evento", "Cita o evento"], ["obligacion", "Pago, renovación o trámite"]];

// Ficha del asunto: Hecho / Deshacer / Nueva fecha y tipo (evento u obligación).
export const CierreAsunto = ({ m, abrirFecha = false }) => {
  const { cierreAsunto } = useData();
  const marcar = useMarcarHecho();
  const [fecha, setFecha] = useState(abrirFecha);
  const hecho = m.status === STATUS.DONE;
  const tipo = async (t) => {
    try { await cierreAsunto(m.id, { accion: "tipo", tipo: t }); } catch { toast.error("No se pudo cambiar."); }
  };
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3" data-testid="cierre-asunto">
      {hecho ? (
        <VolverEnCurso m={m} testid="cierre-deshacer" className="w-full" />
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          <button data-testid="cierre-hecho" onClick={() => marcar(m)} className={`${btn} bg-[#3A6B56] text-white`}><Icons.Check size={18} /> Hecho</button>
          <button data-testid="cierre-nueva-fecha" onClick={() => setFecha(!fecha)} className={`${btn} bg-[#F2EDE4] text-[#1C2421] border border-[#E5DFD5]`}><Icons.CalendarPlus size={18} /> Nueva fecha</button>
        </div>
      )}
      {fecha && !hecho && <NuevaFecha m={m} onDone={() => setFecha(false)} />}
      {m.due_date && !hecho && !m.recordatorio_puntual && (
        <div>
          <p className="text-sm font-semibold text-[#4A5550] mb-1.5">¿Qué es?</p>
          <div className="flex gap-2" data-testid="cierre-tipo">
            {TIPOS.map(([v, l]) => (
              <button key={v} data-testid={`cierre-tipo-${v}`} onClick={() => tipo(v)}
                className={`flex-1 min-h-[48px] px-2 rounded-xl text-sm font-semibold border ${m.tipo === v ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>{l}</button>
            ))}
          </div>
          <p className="text-xs text-[#6E7A74] mt-1.5">Una cita pasada va al historial; un pago o trámite vencido sigue en «Requiere atención» hasta que lo marques como hecho.</p>
        </div>
      )}
    </div>
  );
};
